import { Alert, Box } from "@mui/material"
import React, { Suspense, lazy, useContext, useState } from "react"
import AtLocation from "../../components/Job/AtLocation"
import Completed from "../../components/Job/Completed"
import EnRoute from "../../components/Job/EnRoute"
import Processing from "../../components/Job/Processing"
import { CenterCircular } from "../../components/Loading"
import { useJobContext } from "../../contexts/JobContext"
import { EJobStatus, IJob } from "../../entities/job/interface"

import { useNavigate } from "react-router"
import ShiftContext from "../../contexts/ShiftContext"
import { jobInPickupPhase, useBulkUpdateStatusHook } from "../../entities/job/helper"
import HamburgerMenu from "../../components/HamburgerMenu/HamburgerMenu"
import JobDetailModal from "../../components/JobModals/JobDetailModal"
import JobDocuments from "../../components/Job/JobDocuments"
import Consecutive from "../../components/Job/Consecutive"
import RoadMap from "../../components/JobModals/RoadMap"
import JobBackNavigation from "../../components/JobBackNavigation"

const MapComponent = lazy(() => import("../../components/Map/Map"))
// Define the JobTileProps interface properly

const Job = () => {
    //custom hook
    const {
        currentJobs: jobs,
        isRoadMap,
        setCurrentJobStatus: setJobStatus,
        currentMapState,
        isConsecutive,
    } = useJobContext()

    const shiftContext = useContext(ShiftContext)

    //states
    const [showBackNav, setShowBackNav] = useState<boolean>(true)
    const [openJobDetailModal, setOpenJobDetailModal] = useState(false)
    const [openDocuments, setOpenDocuments] = useState(false)
    const [openRoadMap, setOpenRoadMap] = useState(false)
    const context = useContext(ShiftContext)
    const { handleBulkJobsUpdate } = useBulkUpdateStatusHook()
    const navigate = useNavigate()
    // Handle context missing
    if (!context) {
        throw new Error("ShiftContext is not provided")
    }

    if (!shiftContext) {
        throw new Error("Shift details could not be not populated. Please check your network connection")
    }

    // Show a loading state if `job` is not yet available
    if (!jobs || !jobs.length) {
        return <CenterCircular />
    }

    const inProgressJob = jobs[jobs.length - 1]
    const status = inProgressJob?.jobStatus

    // const completedJobs = shiftContext?.completedJobs
    const { stops: loadedJobs } = context

    const handleBackNav = (isShow: boolean) => {
        setShowBackNav(isShow)
    }

    const handleIncorrectStatus = () => {
        // Handle incorrect status
        if (!jobs) {
            navigate("/job-listing")
            return
        }
    }

    // Show a loading state if `job` is not yet available
    if (!jobs || jobs.length === 0) {
        return <CenterCircular />
    }

    // Render different components based on jobStatus
    const renderJobStatusComponent = () => {
        if (isConsecutive) {
            return <Consecutive />
        }

        if (isRoadMap) {
            return <RoadMap open={true} onClose={() => setOpenRoadMap(false)} />
        }

        switch (status) {
            case EJobStatus.onRouteToPickup:
                return (
                    <EnRoute address={inProgressJob?.originAddress.address} company={inProgressJob?.origin.company} />
                )
            case EJobStatus.atPickupLocation:
                return (
                    <AtLocation
                        updateJobStatus={setJobStatus}
                        jobStatus={EJobStatus.loading}
                        address={inProgressJob?.originAddress.address}
                        company={inProgressJob?.origin?.company}
                        {...(!currentMapState && { isNotAtLocation: "not" })}
                        stage={"pickup"}
                        stageAction={"load"}
                        currentMapState={currentMapState}
                    />
                )
            case EJobStatus.loading:
                return <Processing updateJobStatus={setJobStatus} stage={"pickup"} jobStatus={EJobStatus.loaded} />
            case EJobStatus.loaded:
                return (
                    <Completed
                        stage={"pickup"}
                        jobStatus={EJobStatus.onRouteToDelivery}
                        handleBackNav={handleBackNav}
                    />
                )
            case EJobStatus.onRouteToDelivery:
                return (
                    <EnRoute
                        address={inProgressJob?.destinationAddress.address}
                        company={inProgressJob?.destination.company}
                    />
                )
            case EJobStatus.atDeliveryLocation:
                return (
                    <AtLocation
                        updateJobStatus={setJobStatus}
                        jobStatus={EJobStatus.unloading}
                        address={inProgressJob?.destinationAddress.address}
                        company={inProgressJob?.destination.company}
                        {...(!currentMapState && { isNotAtLocation: "not" })}
                        stage={"delivery"}
                        stageAction={"unload"}
                        currentMapState={currentMapState}
                    />
                )
            case EJobStatus.unloading:
                return <Processing updateJobStatus={setJobStatus} stage={"delivery"} jobStatus={EJobStatus.unloaded} />
            case EJobStatus.unloaded:
                return <Completed stage={"delivery"} jobStatus={EJobStatus.completed} handleBackNav={handleBackNav} />
            default:
                handleIncorrectStatus()
        }
    }

    const isPickupPhase = jobInPickupPhase(inProgressJob?.jobStatus)

    const destinationCoordinates = isPickupPhase
        ? inProgressJob?.originAddress?.gps.coordinates
        : inProgressJob?.destinationAddress?.gps.coordinates

    const markerData = {
        company: isPickupPhase ? inProgressJob?.origin?.company : inProgressJob?.destination?.company,
        address: isPickupPhase ? inProgressJob?.originAddress?.address : inProgressJob?.destinationAddress?.address,
    }

    const handleJobRoute = async (jobs: IJob[]) => {
        try {
            if (!jobs.length) return

            const jobStatus =
                jobs[0]?.jobStatus === EJobStatus.onRouteToPickup
                    ? EJobStatus.atPickupLocation
                    : EJobStatus.atDeliveryLocation

            const jobIds = jobs.filter((job) => !job.isJobCompleted).map((job) => job._id)
            handleBulkJobsUpdate(jobIds, jobStatus)

            // Set the selected job in the context
            setJobStatus(jobStatus)
        } catch (error) {
            // Handle error: open modal with error message
            console.error(error)
        }
    }

    const handleIconPress = async (iconName: string) => {
        if (iconName == "alert") {
            await handleJobRoute(jobs)
        } else if (iconName == "info") {
            setOpenJobDetailModal(!openJobDetailModal)
        } else if (iconName == "documents") {
            setOpenDocuments(!openDocuments)
        } else if (iconName == "roadmap") {
            setOpenRoadMap(!openRoadMap)
        }
    }

    return (
        <Box
            width={"100%"}
            display={"flex"}
            justifyContent={"center"}
            height={"100vh"}
            bgcolor={"primary"}
            position={"fixed"}
        >
            {!isConsecutive && showBackNav && (
                <JobBackNavigation jobs={jobs} stops={loadedJobs} setJobStatus={setJobStatus} />
            )}
            {navigator.onLine && (
                <Suspense fallback={<CenterCircular />}>
                    <MapComponent
                        destinationCoords={destinationCoordinates as [number, number]}
                        job={inProgressJob}
                        markerData={markerData}
                    />
                </Suspense>
            )}
            {inProgressJob?.isJobCompleted && (
                <Alert
                    severity="info"
                    variant="filled"
                    color="success"
                    sx={{
                        position: "absolute",

                        zIndex: 1000,
                    }}
                >
                    This job is completed!
                </Alert>
            )}
            {!isConsecutive && (
                <HamburgerMenu
                    onIconSelect={handleIconPress}
                    isGroupJob={inProgressJob?.groupId ? true : false}
                    jobStatus={inProgressJob?.jobStatus}
                />
            )}
            {openJobDetailModal && (
                <JobDetailModal open={openJobDetailModal} onClose={() => setOpenJobDetailModal(false)} jobs={jobs} />
            )}
            <JobDocuments open={openDocuments} onClose={() => setOpenDocuments(false)} />
            <RoadMap open={openRoadMap} onClose={() => setOpenRoadMap(false)} />
            {renderJobStatusComponent()}
        </Box>
    )
}

export default Job
